/**
 * Rechenbibliothek der Verschiebungsbalken im Panel "Terminaenderungen" - NEUENTWICKLUNG vom
 * 18.10.2026 (Plan `docs/superpowers/plans/2026-10-18-terminaenderungen-verschiebungsbalken.md`,
 * Task 2), KEIN Portierungsmodul: `index.html` kennt keinen Balken, das Modul steht deshalb nicht in
 * `scripts/gen-herkunft.ts#MODULE` (Muster wie `lib/zeitstrahlStufen.ts`, `lib/datumsAnzeige.ts`).
 *
 * Alles hier ist reine Rechnung ohne Rendern: aus einer `TerminZeile` (Backend, `termine` je Sendung)
 * werden Tage, Schwere-Stufe, Zahlentext, Balkengeometrie und die Sortierung der Sendungen. Die
 * Komponente `OverviewPage/Terminaenderungen.tsx` zeichnet nur noch, was hier herauskommt.
 *
 * TAGE rechnet `daysBetweenIsoDates` (`lib/format.ts`) in UTC-Mitternacht: ein Kalenderdatum hat keine
 * Ortszeit, ein Umweg ueber lokale Mitternacht wuerde an der Sommerzeitumstellung 23 oder 25 Stunden
 * messen und beim Teilen durch 24 den Tag verschlucken. In UTC ist jeder Tag exakt 86 400 000 ms.
 * Das Backend liefert die Termine schon auf "YYYY-MM-DD" gekuerzt (`terminZeilen.ts#aufDatumKuerzen`),
 * deshalb genuegt dessen strenge Pruefung; alles andere wird zu `NaN` und damit zu "kein Balken".
 *
 * BEGRIFFE: `gesamt` = Verschiebung seit dem ersten bekannten Termin (ursprung -> aktuell);
 * `bekanntTeil` = davon der Teil, der bei der letzten Kenntnisnahme schon bekannt war (ursprung ->
 * bekannt); `neu` = `gesamt - bekanntTeil`, also was seither dazukam (bekannt -> aktuell, kann negativ
 * sein).
 */
import type { SendungMitTerminaenderungen, TerminZeile } from "../api/terminaenderungen.ts";
import { daysBetweenIsoDates } from "./format.ts";

/** Halbe Balkenbreite in Tagen: Achse in der Mitte, +-14 Tage bis zum Rand, darueber gekappt. */
export const SKALA_TAGE = 14;

/**
 * Schwere nach `gesamt` (Tage), gleich fuer ETA und ETD: `gross` ab 8 (rot), `mittel` 4..7 (gelb),
 * `klein` 1..3 (grau), `null` bei 0 (grau), `frueher` unter 0 (gruen).
 */
export type Stufe = "klein" | "mittel" | "gross" | "null" | "frueher";

export interface Verschiebung {
  gesamt: number;
  neu: number;
  bekanntTeil: number;
  stufe: Stufe;
}

/** Ein Abschnitt des Balkens in Prozent der Balkenbreite (`left`/`width`). */
export interface Abschnitt {
  links: number;
  breite: number;
}

export interface BalkenGeometrie {
  /** Der schon bekannte Teil (Achse -> bekanntTeil); `null`, wenn er leer ist. */
  blass: Abschnitt | null;
  /** Der neue Teil (bekanntTeil -> gesamt); `null`, wenn er leer ist. */
  kraeftig: Abschnitt | null;
  /** Der neue Teil laeuft gegen den bekannten (Hin und Zurueck nach Kenntnisnahme). */
  ruecklaeufig: boolean;
  /**
   * `gesamt` oder `bekanntTeil` liegt jenseits der Skala - am entsprechenden Rand gehoert eine Marke
   * hin. Gehen beide darueber, gibt `gesamt` die Seite an.
   */
  gekappt: "links" | "rechts" | null;
}

/**
 * Ganze Kalendertage von `von` bis `bis` ("YYYY-MM-DD"); negativ, wenn `bis` vor `von` liegt. Ein
 * unlesbares Datum ergibt `NaN` - `verschiebungVon` faengt das ab. Duenne Huelle um
 * `daysBetweenIsoDates` (`lib/format.ts`), nur dass deren `null` hier `NaN` heisst.
 */
export function tageZwischen(von: string, bis: string): number {
  return daysBetweenIsoDates(von, bis) ?? Number.NaN;
}

/** Schwere-Stufe zu einer Gesamtverschiebung in Tagen, siehe `Stufe`. */
export function stufeFuer(gesamt: number): Stufe {
  if (gesamt >= 8) return "gross";
  if (gesamt >= 4) return "mittel";
  if (gesamt >= 1) return "klein";
  if (gesamt === 0) return "null";
  return "frueher";
}

/**
 * Die Verschiebung einer Terminzeile - `null` fuer jede Art ausser `verschiebung` (nur dort gibt es
 * einen Balken) und, defensiv, wenn ein Datum fehlt oder nicht lesbar ist.
 */
export function verschiebungVon(t: TerminZeile): Verschiebung | null {
  if (t.art !== "verschiebung") return null;
  if (t.ursprung === null || t.bekannt === null || t.aktuell === null) return null;
  const gesamt = tageZwischen(t.ursprung, t.aktuell);
  const bekanntTeil = tageZwischen(t.ursprung, t.bekannt);
  if (!Number.isFinite(gesamt) || !Number.isFinite(bekanntTeil)) return null;
  return { gesamt, neu: gesamt - bekanntTeil, bekanntTeil, stufe: stufeFuer(gesamt) };
}

/** Minuszeichen U+2212 statt "-": im Zusatz steht ein echtes Minus, kein Bindestrich. */
const MINUS = "−";

/** Tage mit Vorzeichen: "+5", "−7", "±0" - auch fuer den Screenreader-Text des Panels. */
export function mitVorzeichen(n: number): string {
  if (n > 0) return `+${n}`;
  if (n < 0) return `${MINUS}${-n}`;
  return "±0";
}

/**
 * Zahlentext neben dem Balken. `haupt` ist die Gesamtverschiebung ("+12", "±0", "11 Tage früher"),
 * `zusatz` der Anteil seit der letzten Kenntnisnahme ("(+5 neu)", "(−7 neu)", "(±0 neu)") - `null`,
 * wenn nichts davon schon bekannt war (dann ist alles neu und der Zusatz wuerde nur wiederholen).
 */
export function zahlText(v: Verschiebung): { haupt: string; zusatz: string | null } {
  let haupt: string;
  if (v.gesamt > 0) haupt = `+${v.gesamt}`;
  else if (v.gesamt === 0) haupt = "±0";
  else haupt = `${-v.gesamt} ${v.gesamt === -1 ? "Tag" : "Tage"} früher`;
  const zusatz = v.bekanntTeil === 0 ? null : `(${mitVorzeichen(v.neu)} neu)`;
  return { haupt, zusatz };
}

/**
 * Balkengeometrie in Prozent der Balkenbreite. Die Achse (0 Tage) liegt bei 50 %, ein Tag sind
 * `50 / skala` %; Werte jenseits der Skala werden am Rand gekappt (`gekappt` meldet es, die Zahl im
 * Text bleibt unverfaelscht). `blass` laeuft von der Achse bis `bekanntTeil`, `kraeftig` von dort bis
 * `gesamt`; beide koennen sich bei einem Rueckweg ueberdecken (`ruecklaeufig`).
 *
 * AUSNAHME gleichlaufend und gekappt (`bekanntTeil` und `neu` in dieselbe Richtung, `|gesamt| > skala`):
 * der Balken reicht von der Achse bis zum Rand, und `blass`/`kraeftig` teilen diese Laenge anteilig
 * nach `|bekanntTeil| : |neu|` - sonst laegen beide am Rand und der neue Teil waere unsichtbar.
 */
export function balkenGeometrie(v: Verschiebung, skala: number = SKALA_TAGE): BalkenGeometrie {
  const pos = (tage: number): number => 50 + (Math.max(-skala, Math.min(skala, tage)) / skala) * 50;
  const strecke = (von: number, bis: number): Abschnitt | null =>
    von === bis ? null : { links: Math.min(von, bis), breite: Math.abs(bis - von) };

  const achse = pos(0);
  const ruecklaeufig = v.bekanntTeil !== 0 && Math.sign(v.neu) === -Math.sign(v.bekanntTeil);
  // Die Seite ist die des Werts, der ueber die Skala geht; gehen beide darueber, zaehlt `gesamt`.
  let gekappt: BalkenGeometrie["gekappt"] = null;
  if (Math.abs(v.gesamt) > skala) gekappt = v.gesamt > 0 ? "rechts" : "links";
  else if (Math.abs(v.bekanntTeil) > skala) gekappt = v.bekanntTeil > 0 ? "rechts" : "links";

  // Gleichlaufend und ueber die Skala: Beide Abschnitte wuerden sonst am Rand zusammenfallen, der neue
  // Teil verschwaende. Der Balken ist auf die halbe Breite gekappt; blass und kraeftig teilen sie im
  // Verhaeltnis |bekanntTeil| : |neu| (bei gleichem Vorzeichen ist |gesamt| genau ihre Summe).
  if (!ruecklaeufig && Math.abs(v.gesamt) > skala) {
    const richtung = v.gesamt > 0 ? 1 : -1;
    const trennung = achse + richtung * (Math.abs(v.bekanntTeil) / Math.abs(v.gesamt)) * 50;
    return {
      blass: strecke(achse, trennung),
      kraeftig: strecke(trennung, achse + richtung * 50),
      ruecklaeufig,
      gekappt,
    };
  }

  const bekannt = pos(v.bekanntTeil);
  const ende = pos(v.gesamt);
  return { blass: strecke(achse, bekannt), kraeftig: strecke(bekannt, ende), ruecklaeufig, gekappt };
}

function zeitpunkt(iso: string): number {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
}

/** Sortierschluessel einer Sendung: groesstes positives `gesamt` ihrer Verschiebungen, sonst 0. */
function schwere(s: SendungMitTerminaenderungen): number {
  let max = 0;
  for (const t of s.termine) {
    const verschiebung = verschiebungVon(t);
    if (verschiebung !== null && verschiebung.gesamt > max) max = verschiebung.gesamt;
  }
  return max;
}

/**
 * Neueste `erkanntAm` der Termine einer Sendung; ohne Termine 0 ("aeltestmoeglich", die Sendung
 * sortiert ans Ende). Jede Sendung im Panel hat mindestens eine `TerminZeile`, ein Rueckfall auf die
 * Rohzeilen (`aenderungen`) braeuchte keinen Aufrufer.
 */
function neuesteErkennung(s: SendungMitTerminaenderungen): number {
  return s.termine.reduce((max, t) => Math.max(max, zeitpunkt(t.erkanntAm)), 0);
}

/**
 * Sendungen nach Schwere: absteigend nach groesstem positivem `gesamt` ueber alle Verschiebungen der
 * Sendung; Sendungen ohne positiven Wert danach; bei Gleichstand die mit der neuesten `erkanntAm`
 * zuerst. Liefert eine neue Liste, die Eingabe bleibt unveraendert.
 */
export function sendungenSortieren(s: readonly SendungMitTerminaenderungen[]): SendungMitTerminaenderungen[] {
  return s
    .map((sendung) => ({ sendung, schwere: schwere(sendung), erkannt: neuesteErkennung(sendung) }))
    .sort((a, b) => b.schwere - a.schwere || b.erkannt - a.erkannt)
    .map((e) => e.sendung);
}
